import React, { useEffect, useState } from 'react';
import { getOperatorLeaderboard } from '../api/leaderboardApi';
import Card from '../components/ui/Card';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { Trophy, TrendingUp, Hash } from 'lucide-react';

const S = {
  header: { marginBottom: 'var(--space-xl)' },
  title: {
    fontFamily: 'var(--font-display)', fontSize: '1.75rem', fontWeight: 700,
    color: 'var(--color-text-primary)', margin: 0, letterSpacing: '0.03em',
  },
  subtitle: { fontSize: '0.9rem', color: 'var(--color-text-muted)', margin: '4px 0 0' },
  centered: { display: 'flex', justifyContent: 'center', padding: 'var(--space-2xl)' },
  statsRow: {
    display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)', flexWrap: 'wrap',
  },
  statCard: {
    flex: 1, minWidth: '140px',
    padding: 'var(--space-md)',
    backgroundColor: 'var(--color-bg-card)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid rgba(212,175,55,0.1)',
    textAlign: 'center',
  },
  statValue: {
    fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 700,
    color: 'var(--color-primary)', margin: 0,
  },
  statLabel: {
    fontSize: '0.75rem', color: 'var(--color-text-muted)',
    fontFamily: 'var(--font-display)', letterSpacing: '0.08em', textTransform: 'uppercase',
    marginTop: '4px',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    fontFamily: 'var(--font-display)', fontSize: '0.72rem', fontWeight: 600,
    color: 'var(--color-text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase',
    padding: '10px 12px', borderBottom: '1px solid rgba(212,175,55,0.1)',
    textAlign: 'left',
  },
  td: {
    fontFamily: 'var(--font-body)', fontSize: '0.88rem',
    color: 'var(--color-text-primary)', padding: '10px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.03)',
  },
  rank: {
    fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--color-primary)',
    width: '40px',
  },
  prize: { fontFamily: 'var(--font-mono)', fontWeight: 600 },
  badge: {
    fontSize: '0.7rem', padding: '2px 8px', borderRadius: '9999px',
    fontFamily: 'var(--font-display)', letterSpacing: '0.05em',
  },
  empty: {
    textAlign: 'center', padding: 'var(--space-2xl)',
    color: 'var(--color-text-muted)', fontSize: '0.9rem',
  },
};

function formatEuro(val) {
  return val.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: val < 1 ? 2 : 0, maximumFractionDigits: 2 });
}

export default function AdminLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await getOperatorLeaderboard();
        setData(result);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div style={S.centered}><LoadingSpinner size="lg" /></div>;

  const { leaderboard = [], stats = {} } = data || {};

  return (
    <div>
      <div style={S.header}>
        <h1 style={S.title}>Classifica</h1>
        <p style={S.subtitle}>I migliori giocatori delle tue partite</p>
      </div>

      <div style={S.statsRow}>
        <div style={S.statCard}>
          <div style={S.statValue}>{stats.total_games || 0}</div>
          <div style={S.statLabel}>Partite</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statValue}>{stats.total_prize ? formatEuro(stats.total_prize) : '0'}</div>
          <div style={S.statLabel}>Premi totali</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statValue}>{stats.avg_prize ? formatEuro(stats.avg_prize) : '0'}</div>
          <div style={S.statLabel}>Media premio</div>
        </div>
      </div>

      <Card header="Top giocatori">
        {leaderboard.length === 0 ? (
          <div style={S.empty}>Nessuna partita completata ancora.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>#</th>
                  <th style={S.th}>Giocatore</th>
                  <th style={{ ...S.th, textAlign: 'right' }}>Premio</th>
                  <th style={S.th}>Esito</th>
                  <th style={S.th}>Data</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((entry) => (
                  <tr key={`${entry.rank}-${entry.player_name}`}>
                    <td style={{ ...S.td, ...S.rank }}>{entry.rank}</td>
                    <td style={S.td}>{entry.player_name}</td>
                    <td style={{ ...S.td, ...S.prize, textAlign: 'right' }}>{formatEuro(entry.prize)}</td>
                    <td style={S.td}>
                      <span style={{
                        ...S.badge,
                        backgroundColor: entry.offer_accepted ? 'rgba(46,204,113,0.12)' : 'rgba(52,152,219,0.12)',
                        color: entry.offer_accepted ? 'var(--color-success)' : 'var(--color-info)',
                      }}>
                        {entry.offer_accepted ? 'Offerta' : 'Pacco'}
                      </span>
                    </td>
                    <td style={{ ...S.td, color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>
                      {entry.date ? new Date(entry.date).toLocaleDateString('it-IT') : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
